/**
 * SearchInput — SSOT for page/list/table/entity search experiences.
 * Composes atoms/Input with a leading search icon and optional clear control.
 * Not for command palette or combobox-header filters (documented exceptions).
 */

import { useState, useEffect, useRef, type Ref } from 'react';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import { Button } from './atoms/Button';
import { Input, type InputSize } from './atoms/Input';
import { getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import type { GTMTrackingAttributes } from '../../utils/gtm/gtm-tracking';

/**
 * Where the toggle collapses the field to an icon button:
 * `sm` = below the `sm` viewport; `@4xl` = below `@4xl` of the nearest `@container`.
 */
export type SearchInputToggleAt = 'sm' | '@4xl';

const TOGGLE_VISIBILITY: Record<SearchInputToggleAt, { toggle: string; field: string }> = {
  sm: { toggle: 'sm:!hidden', field: 'max-sm:hidden' },
  '@4xl': { toggle: '@4xl:!hidden', field: '@max-4xl:hidden' },
};

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceDelay?: number;
  onDebouncedChange?: (value: string) => void;
  showClearButton?: boolean;
  /** Collapse to an icon button below `toggleAt`; the icon expands the field. */
  mobileToggle?: boolean;
  /** Collapse point for `mobileToggle`. Default "sm". */
  toggleAt?: SearchInputToggleAt;
  /** Controlled expanded state for `mobileToggle` (e.g. to hide sibling controls). */
  expanded?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  className?: string;
  /** Forwarded to the underlying Input atom. Default "md". */
  size?: InputSize;
  /** Forwarded to the Input atom. `false` = fixed height, aligned with Button of the same size. */
  responsive?: boolean;
  autoFocus?: boolean;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** Optional GTM tracking attributes */
  gtmTracking?: GTMTrackingAttributes;
}

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  placeholder: string;
  showClearButton: boolean;
  hasValue: boolean;
  hasActiveQuery: boolean;
  size: InputSize;
  responsive: boolean;
  autoFocus?: boolean;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  gtmAttrs: Record<string, string>;
  className?: string;
  inputRef?: Ref<HTMLInputElement>;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
}

function SearchField({
  value,
  onChange,
  onClear,
  placeholder,
  showClearButton,
  hasValue,
  hasActiveQuery,
  size,
  responsive,
  autoFocus,
  onFocus,
  onBlur,
  gtmAttrs,
  className,
  inputRef,
  containerProps,
}: SearchFieldProps) {
  const { t } = useTranslation('common');
  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  // cn() does not merge Tailwind — Input size classes include px-* which would
  // otherwise win over pl/pr. Mirror InputGroup’s !pl- / !pr- tokens.
  const startPad = size === 'sm' ? '!pl-8' : '!pl-9';
  const endPad = showClearButton && hasValue ? '!pr-9' : undefined;

  return (
    <div {...containerProps} className={cn('relative', className)}>
      <Search
        className={cn(
          'pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-dark-invirtus/40',
          iconSize
        )}
        aria-hidden
      />
      <Input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        rounded="full"
        size={size}
        responsive={responsive}
        autoFocus={autoFocus}
        className={cn(
          startPad,
          endPad,
          hasActiveQuery && 'border-purple bg-purple/5'
        )}
        aria-label={placeholder}
        {...gtmAttrs}
      />
      {showClearButton && hasValue && (
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<X className={iconSize} />}
          onClick={onClear}
          aria-label={t('actions.clear_search')}
          className="!absolute right-1.5 top-1/2 z-10 -translate-y-1/2 !p-1 text-dark-invirtus/40 hover:text-dark-invirtus"
        />
      )}
    </div>
  );
}

/**
 * Reusable search field — single source of truth for search UI.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  debounceDelay = 200,
  onDebouncedChange,
  showClearButton = false,
  mobileToggle = false,
  toggleAt = 'sm',
  expanded,
  onExpandedChange,
  className,
  size = 'md',
  responsive = true,
  autoFocus,
  onFocus,
  onBlur,
  gtmTracking,
}: SearchInputProps) {
  const gtmAttrs = gtmTracking ? getGTMAttributes(gtmTracking) : {};
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const isExpanded = expanded ?? internalExpanded;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
      onDebouncedChange?.(value);
    }, debounceDelay);
    return () => clearTimeout(timer);
  }, [value, debounceDelay, onDebouncedChange]);

  const setExpanded = (next: boolean) => {
    if (expanded === undefined) setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  const focusInput = () => requestAnimationFrame(() => inputRef.current?.focus());

  const collapseIfEmpty = (shouldReturnFocus: boolean) => {
    if (!mobileToggle || !isExpanded || value.trim()) return;
    setExpanded(false);
    if (shouldReturnFocus) requestAnimationFrame(() => toggleRef.current?.focus());
  };

  const hasValue = value.trim().length > 0;
  const hasActiveQuery = debouncedValue.trim().length > 0;

  const fieldProps = {
    value,
    onChange,
    onClear: () => {
      onChange('');
      // Clearing unmounts the clear button; keep focus in the field.
      focusInput();
    },
    placeholder,
    showClearButton,
    hasValue,
    hasActiveQuery,
    size,
    responsive,
    autoFocus,
    onFocus,
    onBlur,
    gtmAttrs,
    inputRef,
  };

  if (!mobileToggle) {
    return <SearchField {...fieldProps} className={className} />;
  }

  const visibility = TOGGLE_VISIBILITY[toggleAt];

  return (
    <>
      <Button
        ref={toggleRef}
        type="icon"
        intent="muted"
        size={size}
        icon={<Search />}
        aria-label={placeholder}
        aria-expanded={isExpanded}
        className={cn(visibility.toggle, isExpanded && '!hidden')}
        onClick={() => {
          setExpanded(true);
          focusInput();
        }}
      />
      <SearchField
        {...fieldProps}
        className={cn(!isExpanded && visibility.field, className)}
        containerProps={{
          onBlur: (event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              collapseIfEmpty(false);
            }
          },
          onKeyDown: (event) => {
            if (event.key === 'Escape') collapseIfEmpty(true);
          },
        }}
      />
    </>
  );
}

export type { InputSize };
